"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { MOCK_STORES } from "@/lib/mock-data/products";
import { cn } from "@/lib/utils";

const CATEGORY_FALLBACK = [
  "Electrónica", "Celulares", "Computación", "Supermercado",
  "Lácteos", "Medicamentos", "Belleza", "Hogar", "Ropa", "Deportes",
];

interface ComparadorFiltersProps {
  selectedStore?: string;
  selectedCategory?: string;
  showCashback?: boolean;
  /** Admin-managed category list. Falls back to a static list if empty. */
  categories?: ReadonlyArray<{ name: string; emoji?: string | null }>;
}

export function ComparadorFilters({ selectedStore, selectedCategory, showCashback = true, categories }: ComparadorFiltersProps) {
  const catItems =
    categories && categories.length > 0
      ? categories.map((c) => ({ name: c.name, emoji: c.emoji ?? null }))
      : CATEGORY_FALLBACK.map((n) => ({ name: n, emoji: null }));
  const router = useRouter();
  const searchParams = useSearchParams();

  const updateFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    router.push(`/comparador?${params.toString()}`);
  };

  return (
    <div className="space-y-5">
      {/* Sort */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Ordenar por</h3>
        <div className="space-y-1">
          {[
            { value: "relevance", label: "Más relevantes" },
            { value: "price_asc", label: "Menor precio" },
            { value: "price_desc", label: "Mayor precio" },
            { value: "discount", label: "Mayor descuento" },
            { value: "trending", label: "Más buscados" },
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => updateFilter("sort", value)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                searchParams.get("sort") === value
                  ? "bg-primary/10 text-primary font-medium"
                  : "hover:bg-muted text-muted-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Categoría</h3>
        <div className="space-y-1">
          <button
            onClick={() => updateFilter("categoria", undefined)}
            className={cn(
              "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
              !selectedCategory ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"
            )}
          >
            Todas las categorías
          </button>
          {catItems.map((cat) => (
            <button
              key={cat.name}
              onClick={() => updateFilter("categoria", cat.name)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2",
                selectedCategory === cat.name ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"
              )}
            >
              {cat.emoji && <span aria-hidden>{cat.emoji}</span>}
              <span className="truncate">{cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stores */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Tiendas</h3>
        <div className="space-y-1">
          <button
            onClick={() => updateFilter("tienda", undefined)}
            className={cn(
              "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
              !selectedStore ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"
            )}
          >
            Todas las tiendas
          </button>
          {MOCK_STORES.map((store) => (
            <button
              key={store.id}
              onClick={() => updateFilter("tienda", store.slug)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between",
                selectedStore === store.slug ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"
              )}
            >
              {store.name}
              {showCashback && store.cashbackRate && (
                <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">
                  {store.cashbackRate}% CB
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Price range */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Rango de precio</h3>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            placeholder="Min"
            className="flex-1 h-8 px-2 rounded border border-input text-xs bg-background"
            onChange={(e) => updateFilter("precioMin", e.target.value)}
          />
          <span className="text-muted-foreground text-xs">—</span>
          <input
            type="number"
            placeholder="Max"
            className="flex-1 h-8 px-2 rounded border border-input text-xs bg-background"
            onChange={(e) => updateFilter("precioMax", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
